import { FunctionComponent, JSX, useState } from 'react'
import { FilePreview } from '../Dialog/postDialog'
import styles from './filePreview.module.scss'
import DescriptionIcon from '@mui/icons-material/Description'
import { Close } from '@mui/icons-material'
import { useMutation } from '@apollo/client'
import { DELETE_MEDIA } from '@/modules/API/news/news'
import { MediaType } from '@/types/NewsFeed'
import Loading from '@/components/Loading/Loading.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useTranslation } from 'react-i18next'

interface FilePreviewerProps {
    daycareId?: number
    filePreview: Array<FilePreview>
    setFilePreview: (filePreview: Array<FilePreview>) => void
    needToPreview: boolean
}

const FilePreviewer: FunctionComponent<FilePreviewerProps> = ({
    daycareId,
    filePreview,
    setFilePreview,
    needToPreview
}: FilePreviewerProps) => {
    const { t } = useTranslation()
    const { showNotificationError } = useNotification()

    const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set())
    const [deleteMedia] = useMutation(DELETE_MEDIA)

    function handleRemoveImage(index: number): void {
        const mediaId: number = filePreview[index].mediaId
        if (deletingIds.has(mediaId)) return

        setDeletingIds((prev: Set<number>): Set<number> => new Set(prev).add(mediaId))
        deleteMedia({
            variables: { daycareId: daycareId, mediaId },
            onError: () => {
                showNotificationError(t('deleteMediaError'))
                setDeletingIds((prev: Set<number>): Set<number> => {
                    const next: Set<number> = new Set(prev)
                    next.delete(mediaId)
                    return next
                })
            }
        }).then((): void => {
            URL.revokeObjectURL(filePreview[index].preview)
            const newFilePreview: FilePreview[] = [...filePreview]
            newFilePreview.splice(index, 1)
            setFilePreview(newFilePreview)
        })
    }

    return (
        <div className={styles.MaxSize}>
            {filePreview.length === 0 && needToPreview && <Loading size={'small'} />}
            {filePreview.length > 0 && (
                <div className={styles.FilePreviewContainer}>
                    {filePreview.map(
                        (file: FilePreview, index: number): JSX.Element => (
                            <div key={file.mediaId + '_' + index} className={styles.FilePreview}>
                                {file.mediaType === MediaType.PDF ? (
                                    <a
                                        className={styles.DocumentPreview}
                                        href={file.preview}
                                        target='_blank'
                                        rel='noreferrer'
                                    >
                                        <DescriptionIcon className={styles.DocumentIcon} />
                                        <p className={styles.DocumentTitle}>{file.file.name}</p>
                                    </a>
                                ) : file.mediaType === MediaType.VIDEO ? (
                                    <>
                                        <video className={styles.VideoPreview} controls>
                                            <source src={file.preview} type='video/mp4' />
                                        </video>
                                    </>
                                ) : (
                                    <img src={file.preview} className={styles.ImagePreview} alt={file.file.name} />
                                )}
                                <Close
                                    className={styles.CloseIcon}
                                    onClick={() => handleRemoveImage(index)}
                                    style={{
                                        opacity: deletingIds.has(file.mediaId) ? 0.4 : 1,
                                        pointerEvents: deletingIds.has(file.mediaId) ? 'none' : 'auto'
                                    }}
                                />
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    )
}

export default FilePreviewer
